import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { AdminPageHeader, StateBlock, StatusBadge } from "../components/AdminUi";
import SocialChannelPreview from "./SocialChannelPreview";
import {
  BRANDS,
  LOCKED_STATUSES,
  SOCIAL_PLATFORMS,
  createdStamp,
  formatWhen,
  fromLocalInput,
  platformLabel,
  socialApi,
  statusMeta,
  toLocalInput,
} from "./socialMarketingApi";

const REQUEST_META = {
  inviato: { label: "Inviata al bot", tone: "info" },
  ricevuto: { label: "Ricevuta dal bot", tone: "accent" },
  completato: { label: "Completata", tone: "success" },
  errore: { label: "Errore", tone: "danger" },
};

const FILTERS = [
  ["tutti", "Tutti"],
  ["in_revisione", "In revisione"],
  ["approvato", "Approvati"],
  ["programmato", "Programmati"],
  ["pubblicato", "Pubblicati"],
  ["rifiutato", "Rifiutati"],
  ["errore", "Errori"],
];

function splitMedia(text) {
  return String(text || "").split("\n").map((line) => line.trim()).filter(Boolean);
}

function emptyForm(integrations) {
  return {
    id: null,
    title: "",
    content: "",
    mediaText: "",
    variants: [],
    previewAs: "instagram",
    scheduled_at: "",
    notes: "",
    targetIds: integrations.length === 1 ? [integrations[0].id] : [],
    knownTargets: [],
    status: "in_revisione",
    source_name: "",
    revision: 1,
    external_ref: "",
    rejected_reason: "",
    postiz_error: "",
    last_webhook: null,
    approved_by: "",
    approved_at: null,
    postiz_post_ids: [],
    meta_jobs: [],
    meta_error: "",
    published_at: null,
    created_at: null,
  };
}

function metaChannels(link, form) {
  if (!link?.active || !form) return [];
  const chosen = (form.variants || []).map((item) => item.platform);
  return [...new Set(chosen.filter((id) => (
    (id === "facebook" && link.facebook) || (id === "instagram" && link.instagram)
  )))];
}

function toForm(post) {
  const variants = (post.variants || []).map((item) => ({
    platform: item.platform,
    label: item.label || platformLabel(item.platform),
    content: item.content || "",
    mediaText: (item.media_urls || []).join("\n"),
  }));
  return {
    ...emptyForm([]),
    id: post.id,
    title: post.title || "",
    content: post.content || "",
    mediaText: (post.media_urls || []).join("\n"),
    variants,
    previewAs: variants[0]?.platform || "instagram",
    scheduled_at: toLocalInput(post.scheduled_at),
    notes: post.notes || "",
    targetIds: (post.targets || []).map((item) => item.id),
    knownTargets: post.targets || [],
    status: post.status,
    source_name: post.source_name || "",
    revision: post.revision || 1,
    external_ref: post.external_ref || "",
    rejected_reason: post.rejected_reason || "",
    postiz_error: post.postiz_error || "",
    last_webhook: post.last_webhook || null,
    approved_by: post.approved_by || "",
    approved_at: post.approved_at || null,
    postiz_post_ids: post.postiz_post_ids || [],
    meta_jobs: post.meta_jobs || [],
    meta_error: post.meta_error || "",
    published_at: post.published_at || null,
    created_at: post.created_at || null,
  };
}

function webhookText(last) {
  if (!last) return "Dopo l'approvazione il bot legge il post dalla coda degli approvati.";
  if (last.sent) return "Il bot è stato avvisato dell'ultima versione approvata.";
  if (last.reason) return "Il bot legge la coda degli approvati quando controlla i post da pubblicare.";
  return `L'avviso al bot ha restituito un errore (${last.error || "errore"}). Il bot può comunque leggere la coda.`;
}

export default function SocialMarketingBrandModule({ brand }) {
  const meta = BRANDS[brand];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [prompt, setPrompt] = useState("");
  const [savedPrompt, setSavedPrompt] = useState("");
  const [posts, setPosts] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [metaLink, setMetaLink] = useState({ active: false, facebook: false, instagram: false });
  const [filter, setFilter] = useState("tutti");
  const [channelFilter, setChannelFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState("");
  const [grokUrl, setGrokUrl] = useState("");
  const [savedGrokUrl, setSavedGrokUrl] = useState("");
  const [grokKey, setGrokKey] = useState("");
  const [grokMeta, setGrokMeta] = useState({ key_set: false, key_preview: "" });
  const [requests, setRequests] = useState([]);
  const [createInstruction, setCreateInstruction] = useState("");
  const [reviseInstruction, setReviseInstruction] = useState("");
  const watchRequest = useRef("");

  const applyInbox = useCallback((data) => {
    const nextRequests = data.requests || [];
    setPrompt((current) => (current === savedPrompt ? data.prompt || "" : current));
    setSavedPrompt(data.prompt || "");
    setGrokUrl((current) => (current === savedGrokUrl ? data.grok?.url || "" : current));
    setSavedGrokUrl(data.grok?.url || "");
    setGrokMeta({ key_set: Boolean(data.grok?.key_set), key_preview: data.grok?.key_preview || "" });
    setRequests(nextRequests);
    setPosts(data.posts || []);
    setIntegrations(data.integrations || []);
    setMetaLink(data.meta || { active: false, facebook: false, instagram: false });
    const watched = watchRequest.current;
    if (!watched) return data;
    const request = nextRequests.find((item) => item.id === watched);
    if (request?.status === "completato" && request.post_id) {
      const fresh = (data.posts || []).find((item) => item.id === request.post_id);
      if (fresh) {
        setSelectedId(fresh.id);
        setForm(toForm(fresh));
        setReviseInstruction("");
        watchRequest.current = "";
      }
    } else if (request?.status === "errore") {
      watchRequest.current = "";
    }
    return data;
  }, [savedPrompt, savedGrokUrl]);

  const load = useCallback(async () => {
    setError("");
    const data = await socialApi(`/posts?brand=${encodeURIComponent(brand)}`);
    return applyInbox(data);
  }, [brand, applyInbox]);

  const waitingForBot = requests.some((item) => item.status === "inviato" || item.status === "ricevuto");

  useEffect(() => {
    if (!waitingForBot) return undefined;
    const timer = window.setInterval(() => {
      load().catch(() => {});
    }, 8000);
    const stop = window.setTimeout(() => window.clearInterval(timer), 180000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [waitingForBot, load]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    socialApi(`/posts?brand=${encodeURIComponent(brand)}`)
      .then((data) => {
        if (!active) return;
        setPrompt(data.prompt || "");
        setSavedPrompt(data.prompt || "");
        setGrokUrl(data.grok?.url || "");
        setSavedGrokUrl(data.grok?.url || "");
        setGrokMeta({ key_set: Boolean(data.grok?.key_set), key_preview: data.grok?.key_preview || "" });
        setRequests(data.requests || []);
        setPosts(data.posts || []);
        setIntegrations(data.integrations || []);
        setMetaLink(data.meta || { active: false, facebook: false, instagram: false });
        setError("");
      })
      .catch((err) => {
        if (active) setError(err.message || "Impossibile caricare i post");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [brand]);

  const presentPlatforms = useMemo(() => {
    const map = new Map();
    posts.forEach((post) => (post.variants || []).forEach((item) => {
      if (!map.has(item.platform)) map.set(item.platform, item.label || platformLabel(item.platform));
    }));
    return [...map.entries()].map(([id, label]) => ({ id, label }));
  }, [posts]);
  const visible = useMemo(
    () => posts.filter((post) => {
      if (filter !== "tutti" && post.status !== filter) return false;
      if (channelFilter && !(post.variants || []).some((item) => item.platform === channelFilter)) return false;
      return true;
    }).sort((left, right) => createdStamp(right) - createdStamp(left)),
    [posts, filter, channelFilter],
  );
  const locked = form ? LOCKED_STATUSES.includes(form.status) : false;
  const metaPending = Boolean((form?.meta_jobs || []).some((job) => job.state === "in_attesa" || job.state === "in_corso"));
  const plannedChannels = metaChannels(metaLink, form);

  const run = async (key, action) => {
    setBusy(key);
    try {
      await action();
    } catch (err) {
      toast.error(err.message || "Operazione non riuscita");
    } finally {
      setBusy("");
    }
  };

  const savePrompt = () => run("prompt", async () => {
    const data = await socialApi(`/prompts/${brand}`, { method: "PUT", body: JSON.stringify({ prompt }) });
    setPrompt(data.prompt || "");
    setSavedPrompt(data.prompt || "");
    toast.success("Indicazioni salvate. Il bot le legge prima di generare i post.");
  });

  const grokDirty = grokUrl !== savedGrokUrl || Boolean(grokKey.trim());
  const grokReady = Boolean(savedGrokUrl && grokMeta.key_set) && !grokDirty;

  const saveGrok = () => run("grok", async () => {
    const payload = { url: grokUrl.trim() };
    if (grokKey.trim()) payload.key = grokKey.trim();
    const data = await socialApi(`/grok/${brand}`, { method: "PUT", body: JSON.stringify(payload) });
    const next = data.grok || {};
    setGrokUrl(next.url || "");
    setSavedGrokUrl(next.url || "");
    setGrokMeta({ key_set: Boolean(next.key_set), key_preview: next.key_preview || "" });
    setGrokKey("");
    toast.success("Routine del bot salvata");
  });

  const sendToBot = (action) => run(action === "crea" ? "create-bot" : "revise-bot", async () => {
    const instruction = action === "crea" ? createInstruction.trim() : reviseInstruction.trim();
    if (!instruction) throw new Error("Scrivi le istruzioni da inviare al bot");
    if (!grokReady) throw new Error("Salva prima l'indirizzo e la chiave della routine Grok di questo marchio");
    const payload = { brand, action, instruction };
    if (action === "modifica") payload.post_id = form?.id;
    const data = await socialApi("/bot-requests", { method: "POST", body: JSON.stringify(payload) });
    watchRequest.current = data.request?.id || "";
    if (action === "crea") setCreateInstruction("");
    else setReviseInstruction("");
    toast.success(action === "crea"
      ? "Istruzioni inviate. Il post compare in revisione quando il bot ha finito."
      : "Istruzioni inviate. Il bot aggiorna questa versione in revisione.");
    await load();
  });

  const payloadFromForm = () => {
    const variants = form.variants.map((item) => ({
      platform: item.platform,
      label: item.label || platformLabel(item.platform),
      content: item.content,
      media_urls: splitMedia(item.mediaText),
    }));
    const media_urls = variants.length
      ? [...new Set(variants.flatMap((item) => item.media_urls))].slice(0, 10)
      : splitMedia(form.mediaText);
    const content = variants.map((item) => item.content.trim()).find(Boolean) || form.content;
    const targets = form.targetIds.map((id) => {
      const known = integrations.find((item) => item.id === id)
        || (form.knownTargets || []).find((item) => item.id === id);
      return known ? { id: known.id, provider: known.provider || "", label: known.label || "" } : { id, provider: "", label: id };
    });
    const payload = { title: form.title, content, media_urls, notes: form.notes, targets, variants };
    if (form.scheduled_at) payload.scheduled_at = fromLocalInput(form.scheduled_at);
    else if (form.id) payload.clear_schedule = true;
    return payload;
  };

  const persist = async () => {
    const payload = payloadFromForm();
    if (!String(payload.content || "").trim()) throw new Error("Il testo del post è obbligatorio");
    if (form.id) {
      const updated = await socialApi(`/posts/${form.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      return updated.post;
    }
    return socialApi("/posts", { method: "POST", body: JSON.stringify({ ...payload, brand }) });
  };

  const refreshOpen = async (post) => {
    await load();
    setSelectedId(post.id);
    setForm(toForm(post));
  };

  const save = () => run("save", async () => {
    const post = await persist();
    toast.success("Post salvato");
    await refreshOpen(post);
  });

  const approve = () => run("approve", async () => {
    const channels = metaChannels(metaLink, form);
    if (channels.length && !form.scheduled_at) {
      toast.error("Indica l'orario di pubblicazione");
      return;
    }
    if (channels.length) {
      const names = channels.map((id) => platformLabel(id)).join(" e ");
      const when = new Date(form.scheduled_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
      const confirmed = window.confirm(
        `Il collegamento Meta è attivo. Approvando, Affidya pubblicherà il post su ${names} il ${when}. Vuoi continuare?`,
      );
      if (!confirmed) return;
    }
    const post = await persist();
    try {
      const result = await socialApi(`/posts/${post.id}/approve`, { method: "POST" });
      const publication = result.meta || {};
      if (publication.active) {
        if (publication.ok) toast.success(publication.message || "Post approvato");
        else toast.error(publication.message || "Pubblicazione su Meta non riuscita");
      } else {
        const webhook = result.webhook || {};
        if (webhook.sent) toast.success("Post approvato. Il bot è stato avvisato.");
        else if (webhook.error) toast.success(`Post approvato. Avviso al bot: ${webhook.error}`);
        else toast.success("Post approvato. Il bot lo trova nella coda degli approvati.");
      }
      await refreshOpen(result.post);
    } catch (err) {
      await refreshOpen(post);
      throw err;
    }
  });

  const reject = () => run("reject", async () => {
    if (!form?.id) return;
    const result = await socialApi(`/posts/${form.id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason: rejectReason }),
    });
    toast.success("Post rifiutato");
    setRejectReason("");
    await refreshOpen(result.post);
  });

  const revoke = () => run("revoke", async () => {
    const result = await socialApi(`/posts/${form.id}/revoke`, { method: "POST" });
    toast.success("Approvazione ritirata. Il post è di nuovo in revisione.");
    await refreshOpen(result.post);
  });

  const remove = () => run("delete", async () => {
    if (!window.confirm("Eliminare questo post?")) return;
    await socialApi(`/posts/${form.id}`, { method: "DELETE" });
    toast.success("Post eliminato");
    setForm(null);
    setSelectedId(null);
    await load();
  });

  const channelChoices = useMemo(() => {
    const map = new Map(integrations.map((item) => [item.id, item]));
    (form?.knownTargets || []).forEach((item) => { if (!map.has(item.id)) map.set(item.id, item); });
    return [...map.values()];
  }, [integrations, form]);

  const socialChoices = useMemo(() => {
    const map = new Map(SOCIAL_PLATFORMS.map((item) => [item.id, item.label]));
    (form?.variants || []).forEach((item) => {
      if (!map.has(item.platform)) map.set(item.platform, item.label || platformLabel(item.platform));
    });
    return [...map.entries()].map(([id, label]) => ({ id, label }));
  }, [form]);

  const activeVariant = form?.variants.find((item) => item.platform === form.previewAs) || null;
  const editorContent = activeVariant ? activeVariant.content : (form?.content || "");
  const editorMedia = activeVariant ? activeVariant.mediaText : (form?.mediaText || "");

  const setEditorContent = (value) => {
    setForm((current) => {
      if (!current) return current;
      const active = current.variants.find((item) => item.platform === current.previewAs);
      if (!active) return { ...current, content: value };
      return {
        ...current,
        variants: current.variants.map((item) => (item.platform === active.platform ? { ...item, content: value } : item)),
      };
    });
  };

  const setEditorMedia = (value) => {
    setForm((current) => {
      if (!current) return current;
      const active = current.variants.find((item) => item.platform === current.previewAs);
      if (!active) return { ...current, mediaText: value };
      return {
        ...current,
        variants: current.variants.map((item) => (item.platform === active.platform ? { ...item, mediaText: value } : item)),
      };
    });
  };

  const toggleSocial = (id, label, checked) => {
    setForm((current) => {
      if (!current) return current;
      if (checked) {
        if (current.variants.some((item) => item.platform === id)) return { ...current, previewAs: id };
        const seed = current.variants[0];
        return {
          ...current,
          previewAs: id,
          variants: [...current.variants, {
            platform: id,
            label,
            content: seed?.content || current.content,
            mediaText: seed?.mediaText || current.mediaText,
          }],
        };
      }
      const variants = current.variants.filter((item) => item.platform !== id);
      const previewAs = variants.some((item) => item.platform === current.previewAs)
        ? current.previewAs
        : (variants[0]?.platform || "instagram");
      return { ...current, variants, previewAs };
    });
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title={meta.label}
        description="I bot Grok caricano un testo e i media per ogni social. Qui si vede l'anteprima del canale, si corregge e si approva con l'orario di pubblicazione."
      />

      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="space-y-1">
            <Label htmlFor="social-prompt">Indicazioni per il bot</Label>
            <p className="text-sm text-admin-muted">
              Il bot di questo marchio legge questo testo prima di generare i contenuti. Può includere tono, temi, cosa evitare e inviti all'azione.
            </p>
          </div>
          <Textarea
            id="social-prompt"
            rows={8}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Esempio: parla alle famiglie in modo chiaro, senza dati di ospiti e senza promesse sanitarie."
          />
          <Button type="button" onClick={savePrompt} disabled={busy === "prompt" || prompt === savedPrompt}>
            Salva indicazioni
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="space-y-1">
            <h3 className="font-medium text-admin-text">Chiedi un post al bot</h3>
            <p className="text-sm text-admin-muted">
              Sul bot Grok di {meta.label} aggiungi una routine con trigger webhook e incolla qui indirizzo e chiave.
              Le istruzioni partono verso quel bot: genera testo, immagini e video e carica il post in revisione.
              In Procedura bot, copia di nuovo il testo del marchio: spiega al bot come creare il post e come aggiornare una versione già in revisione.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="grok-url">Indirizzo della routine</Label>
            <Input id="grok-url" value={grokUrl} onChange={(event) => setGrokUrl(event.target.value)} placeholder="https://" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="grok-key">Chiave della routine</Label>
            <Input
              id="grok-key"
              type="password"
              autoComplete="off"
              value={grokKey}
              onChange={(event) => setGrokKey(event.target.value)}
              placeholder={grokMeta.key_set ? `Salvata (${grokMeta.key_preview}). Scrivi solo per sostituirla.` : "Chiave del webhook"}
            />
          </div>
          <Button type="button" variant="outline" onClick={saveGrok} disabled={busy === "grok" || !grokDirty}>
            Salva routine
          </Button>
          <div className="space-y-1">
            <Label htmlFor="create-instruction">Istruzioni per un nuovo post</Label>
            <Textarea
              id="create-instruction"
              rows={5}
              value={createInstruction}
              onChange={(event) => setCreateInstruction(event.target.value)}
              placeholder="Esempio: un post sulle visite in struttura, tono chiaro, con una foto dell'ingresso e un video breve. Nessun nome di ospite."
            />
          </div>
          <Button type="button" onClick={() => sendToBot("crea")} disabled={busy === "create-bot" || !createInstruction.trim() || !grokReady}>
            Invia al bot
          </Button>
          {!grokReady ? (
            <p className="text-sm text-admin-muted">Per inviare serve la routine salvata, con indirizzo e chiave.</p>
          ) : null}
          {requests.length ? (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-admin-text">Richieste inviate</h4>
              {requests.slice(0, 6).map((item) => {
                const state = REQUEST_META[item.status] || { label: item.status, tone: "neutral" };
                return (
                  <div key={item.id} className="rounded-lg border border-admin-border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
                      <span className="text-xs text-admin-muted">{item.action === "modifica" ? "Modifica" : "Nuovo post"} · {formatWhen(item.created_at)}</span>
                    </div>
                    <p className="mt-2 line-clamp-3 text-sm text-admin-text">{item.instruction}</p>
                    {item.error ? <p className="mt-1 text-sm text-admin-text">{item.error}</p> : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {loading ? <StateBlock state="loading" title="Caricamento dei post…" /> : null}
      {error ? <StateBlock state="error" title="Impossibile caricare i post" description={error} onRetry={() => load().catch((err) => setError(err.message))} /> : null}

      {!loading && !error ? (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]">
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {FILTERS.map(([id, label]) => {
                const count = id === "tutti" ? posts.length : posts.filter((post) => post.status === id).length;
                return (
                  <Button key={id} type="button" size="sm" variant={filter === id ? "default" : "outline"} onClick={() => setFilter(id)}>
                    {label} ({count})
                  </Button>
                );
              })}
            </div>
            {presentPlatforms.length ? (
              <label className="block space-y-1 text-sm text-admin-text">
                <span className="text-admin-muted">Canale</span>
                <select
                  className="h-10 w-full rounded-md border border-admin-border bg-admin-surface px-3 text-sm"
                  value={channelFilter}
                  onChange={(event) => setChannelFilter(event.target.value)}
                >
                  <option value="">Tutti i canali</option>
                  {presentPlatforms.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                </select>
              </label>
            ) : null}
            <Button type="button" variant="outline" onClick={() => { setSelectedId("new"); setForm(emptyForm(integrations)); }}>
              Nuovo post
            </Button>
            {visible.length === 0 ? (
              <StateBlock
                compact
                state="empty"
                title="Nessun post in questo elenco"
                description="Quando un bot Grok genera un contenuto per questo marchio, compare qui in revisione."
              />
            ) : visible.map((post) => {
              const state = statusMeta(post.status, post);
              const active = selectedId === post.id;
              return (
                <button
                  key={post.id}
                  type="button"
                  onClick={() => { setSelectedId(post.id); setForm(toForm(post)); }}
                  className={`block w-full rounded-xl border p-3 text-left ${active ? "border-admin-accent bg-admin-accent-soft" : "border-admin-border bg-admin-surface"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="line-clamp-2 text-sm font-medium text-admin-text">{post.title || post.content}</span>
                    <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
                  </div>
                  <p className="mt-2 text-xs font-medium text-admin-text">
                    Creato il {formatWhen(post.created_at, "data non disponibile")}
                  </p>
                  <p className="mt-1 text-xs text-admin-muted">
                    {(post.variants || []).length
                      ? (post.variants || []).map((item) => item.label || platformLabel(item.platform)).join(" · ")
                      : "Canale non indicato"}
                    {post.scheduled_at ? ` · Pubblicazione ${formatWhen(post.scheduled_at)}` : ""}
                    {" · "}{post.source_name || "Bot Grok"}
                  </p>
                </button>
              );
            })}
          </div>

          {form ? (
            <Card>
              <CardContent className="space-y-4 p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={statusMeta(form.status, form).tone}>{statusMeta(form.status, form).label}</StatusBadge>
                  <span className="text-xs text-admin-muted">Versione {form.revision}{form.source_name ? ` · ${form.source_name}` : ""}{form.external_ref ? ` · rif. ${form.external_ref}` : ""}</span>
                </div>
                {form.id ? (
                  <p className="text-sm text-admin-text">
                    Creato il {formatWhen(form.created_at, "data non disponibile")}
                    {form.scheduled_at ? ` · Pubblicazione ${formatWhen(form.scheduled_at)}` : ""}
                  </p>
                ) : null}
                {form.rejected_reason ? <p className="text-sm text-admin-text">Motivo del rifiuto: {form.rejected_reason}</p> : null}
                {form.meta_error ? <p className="text-sm text-admin-text">Errore Meta: {form.meta_error}</p> : null}
                {form.meta_jobs?.length ? (
                  <p className="text-sm text-admin-muted">
                    Meta: {form.meta_jobs.map((job) => {
                      const state = job.state === "inviato" ? "inviato" : job.state === "errore" ? "errore" : "in programma";
                      return `${job.platform ? platformLabel(job.platform) : "canale"} ${state}${job.remote_id ? ` (${job.remote_id})` : ""}`;
                    }).join(" · ")}
                  </p>
                ) : null}
                {form.postiz_error ? <p className="text-sm text-admin-text">Errore Postiz segnalato dal bot: {form.postiz_error}</p> : null}
                {form.postiz_post_ids?.length ? <p className="text-sm text-admin-muted">ID Postiz: {form.postiz_post_ids.join(", ")}</p> : null}
                <p className="text-sm text-admin-muted">{webhookText(form.last_webhook)}</p>
                {form.approved_by ? <p className="text-xs text-admin-muted">Approvato da {form.approved_by}{form.approved_at ? ` il ${formatWhen(form.approved_at)}` : ""}</p> : null}

                <fieldset className="space-y-2" disabled={locked}>
                  <legend className="text-sm font-medium text-admin-text">Canali social</legend>
                  <p className="text-sm text-admin-muted">Ogni canale ha il proprio testo, le proprie immagini o i propri video e un'anteprima.</p>
                  <div className="flex flex-wrap gap-2">
                    {socialChoices.map((item) => {
                      const checked = form.variants.some((variant) => variant.platform === item.id);
                      return (
                        <label key={item.id} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${checked ? "border-admin-accent bg-admin-accent-soft text-admin-text" : "border-admin-border text-admin-muted"}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) => toggleSocial(item.id, item.label, event.target.checked)}
                          />
                          {item.label}
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
                {form.variants.length ? (
                  <div className="flex flex-wrap gap-2" role="tablist" aria-label="Canale da modificare">
                    {form.variants.map((item) => (
                      <Button
                        key={item.platform}
                        type="button"
                        size="sm"
                        variant={form.previewAs === item.platform ? "default" : "outline"}
                        onClick={() => setForm({ ...form, previewAs: item.platform })}
                      >
                        {item.label || platformLabel(item.platform)}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label htmlFor="preview-as">Guarda l'anteprima come</Label>
                    <select
                      id="preview-as"
                      className="h-10 w-full max-w-xs rounded-md border border-admin-border bg-admin-surface px-3 text-sm"
                      value={form.previewAs}
                      onChange={(event) => setForm({ ...form, previewAs: event.target.value })}
                    >
                      {SOCIAL_PLATFORMS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                    </select>
                  </div>
                )}
                <SocialChannelPreview
                  platform={form.previewAs}
                  account={meta.label}
                  content={editorContent}
                  mediaUrls={splitMedia(editorMedia)}
                  assigned={Boolean(activeVariant)}
                />
                <div className="space-y-1">
                  <Label htmlFor="post-title">Titolo interno</Label>
                  <Input id="post-title" value={form.title} disabled={locked} onChange={(event) => setForm({ ...form, title: event.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="post-content">{activeVariant ? `Testo per ${activeVariant.label || platformLabel(activeVariant.platform)}` : "Testo del post"}</Label>
                  <Textarea id="post-content" rows={8} value={editorContent} disabled={locked} onChange={(event) => setEditorContent(event.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="post-media">{activeVariant ? `Immagini e video per ${activeVariant.label || platformLabel(activeVariant.platform)}, un indirizzo per riga` : "Indirizzi di immagini e video, uno per riga"}</Label>
                  <Textarea id="post-media" rows={3} value={editorMedia} disabled={locked} onChange={(event) => setEditorMedia(event.target.value)} placeholder="https://" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="post-when">Orario di pubblicazione</Label>
                  <Input id="post-when" type="datetime-local" value={form.scheduled_at} disabled={locked} onChange={(event) => setForm({ ...form, scheduled_at: event.target.value })} />
                  <p className="text-xs text-admin-muted">
                    {metaLink.active
                      ? "Con il collegamento Meta attivo, Facebook e Instagram partono da Affidya a questo orario. Gli altri social restano al bot."
                      : "Il bot usa questo orario quando crea il post programmato su Postiz."}
                  </p>
                </div>
                <fieldset className="space-y-2" disabled={locked}>
                  <legend className="text-sm font-medium text-admin-text">Canali Postiz</legend>
                  {channelChoices.length === 0 ? (
                    <p className="text-sm text-admin-muted">Nessun canale collegato per questo marchio. Si configurano nella voce Postiz. L'approvazione resta possibile e il bot sceglie il canale.</p>
                  ) : channelChoices.map((item) => (
                    <label key={item.id} className="flex items-center gap-2 text-sm text-admin-text">
                      <input
                        type="checkbox"
                        checked={form.targetIds.includes(item.id)}
                        onChange={(event) => {
                          const targetIds = event.target.checked
                            ? [...form.targetIds, item.id]
                            : form.targetIds.filter((id) => id !== item.id);
                          setForm({ ...form, targetIds });
                        }}
                      />
                      <span>{item.label || item.provider || item.id}{item.provider ? ` · ${item.provider}` : ""}</span>
                    </label>
                  ))}
                </fieldset>
                <div className="space-y-1">
                  <Label htmlFor="post-notes">Note interne</Label>
                  <Textarea id="post-notes" rows={3} value={form.notes} disabled={locked} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
                </div>

                {form.id && form.status === "in_revisione" ? (
                  <div className="space-y-2 rounded-lg border border-admin-border p-4">
                    <div className="space-y-1">
                      <Label htmlFor="revise-instruction">Istruzioni per modificare questo post</Label>
                      <p className="text-sm text-admin-muted">
                        Il bot riceve il testo, le immagini e i video attuali. Può cambiarli e ricarica questa stessa versione in revisione.
                      </p>
                    </div>
                    <Textarea
                      id="revise-instruction"
                      rows={4}
                      value={reviseInstruction}
                      onChange={(event) => setReviseInstruction(event.target.value)}
                      placeholder="Esempio: accorcia Instagram, sostituisci la foto con l'ingresso e rifai il video in verticale."
                    />
                    <Button type="button" onClick={() => sendToBot("modifica")} disabled={busy === "revise-bot" || !reviseInstruction.trim() || !grokReady}>
                      Invia modifica al bot
                    </Button>
                  </div>
                ) : null}
                {plannedChannels.length > 0 && !locked ? (
                  <p className="text-sm text-admin-text">
                    Collegamento Meta attivo: approvando, il post viene pubblicato su {plannedChannels.map((id) => platformLabel(id)).join(" e ")}
                    {form.scheduled_at ? ` il ${new Date(form.scheduled_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : ""}.
                    Prima della pubblicazione compare un messaggio di conferma.
                  </p>
                ) : null}
                {locked ? (
                  <div className="space-y-3">
                    <p className="text-sm text-admin-muted">
                      {metaPending
                        ? "La pubblicazione su Facebook e Instagram è programmata. Puoi ritirarla finché non parte."
                        : (form.meta_jobs || []).some((job) => job.state === "inviato")
                          ? "Questo post è stato inviato a Meta."
                          : "Questo post è già stato consegnato a Postiz. Una modifica richiede un nuovo caricamento del bot."}
                    </p>
                    {metaPending ? <Button type="button" variant="outline" onClick={revoke} disabled={busy === "revoke"}>Ritira approvazione</Button> : null}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" onClick={save} disabled={busy === "save"}>Salva</Button>
                    <Button type="button" onClick={approve} disabled={busy === "approve"}>Approva</Button>
                    {form.status === "approvato" ? <Button type="button" variant="outline" onClick={revoke} disabled={busy === "revoke"}>Ritira approvazione</Button> : null}
                    {form.id && ["in_revisione", "rifiutato", "errore"].includes(form.status) ? (
                      <Button type="button" variant="outline" onClick={remove} disabled={busy === "delete"}>Elimina</Button>
                    ) : null}
                  </div>
                )}
                {form.id && !locked ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[16rem] flex-1 space-y-1">
                      <Label htmlFor="reject-reason">Motivo del rifiuto</Label>
                      <Input id="reject-reason" value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} placeholder="Facoltativo, il bot può leggerlo" />
                    </div>
                    <Button type="button" variant="outline" onClick={reject} disabled={busy === "reject"}>Rifiuta</Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : (
            <StateBlock state="empty" title="Seleziona un post" description="Oppure creane uno manualmente. I contenuti dei bot arrivano da soli in revisione." />
          )}
        </div>
      ) : null}
    </div>
  );
}
